/**
 * FXServer convars that hold credentials.
 * Admins with console access could otherwise read them straight out of the
 * server, so commands touching them are refused and their values are stripped
 * from everything the console pipeline emits.
 */
export const protectedConvars = [
    'mysql_connection_string',
    'steam_webApiKey',
    'sv_apiSecret',
    'sv_discordToken',
    'sv_licenseKey',
    'sv_logsAPIKey',
    'sv_tebexSecret',
    'sv_uploadAPIKey'
] as const;

const convarPattern = protectedConvars.join('|');

/**
 * Matches a protected convar followed by whatever it is set to, covering the
 * shapes fxserver and its resources print: `name value`, `name = value`,
 * `name: value`, `"name" is "value"`, quoted or not.
 */
const redactionRegex = new RegExp(
    String.raw`("?\b(?:${convarPattern})\b"?)(\s*(?:=|:|\bis\b)?\s*)("[^"\n]*"|'[^'\n]*'|[^\s"',;.][^\s"']*)`,
    'gi'
);

/**
 * Replaces the value of any protected convar found in the given text.
 * NOTE: best effort, the console arrives in chunks so a value split across two
 * writes can slip through - the command block is what actually guarantees an
 * admin cannot ask for them.
 */
export const redactProtectedConvars = (text: string) => {
    if (typeof text !== 'string' || !text.length) return text;
    return text.replace(redactionRegex, (_match, name, separator) => {
        return `${name}${separator || ' '}[REDACTED]`;
    });
};

/**
 * Returns the protected convar an admin command would read or change, if any.
 */
export const findProtectedConvar = (command: string) => {
    if (typeof command !== 'string' || !command.length) return undefined;
    return protectedConvars.find((convar) => {
        return new RegExp(String.raw`\b${convar}\b`, 'i').test(command);
    });
};
